import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useAuth } from '../contexts/AuthContext';
import { claimApi } from '../services/api';

const validProviderId = (raw) => /^[1-9][0-9]*$/.test(raw || '') && Number.isSafeInteger(Number(raw));

const ClaimBusinessPage = () => {
  const { providerId: rawProviderId } = useParams();
  const providerId = validProviderId(rawProviderId) ? Number(rawProviderId) : null;
  const { currentUser, providerData } = useAuth();
  const { t } = useTranslation();
  const [channels, setChannels] = useState(null);
  const [loading, setLoading] = useState(Boolean(currentUser && !providerData && providerId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState(null);
  const [manualSubmitted, setManualSubmitted] = useState(false);
  const [manual, setManual] = useState({ contactName: '', contactEmail: '', contactPhone: '' });

  useEffect(() => {
    if (!currentUser || providerData || !providerId) return;
    let cancelled = false;
    setLoading(true);
    claimApi.getChannels(providerId)
      .then((result) => {
        if (!cancelled) setChannels(Array.isArray(result) ? result : []);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setError(requestError?.status === 404
            ? t('claim.listingGone')
            : t('claim.loadFailed'));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, providerData, providerId]);

  const emailChannel = useMemo(
    () => channels?.find((channel) => channel.channel === 'EMAIL' && channel.automated),
    [channels],
  );

  const sendClaimEmail = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await claimApi.requestProviderClaim(providerId);
      setSentTo(response.maskedDestination || emailChannel?.masked || t('claim.businessEmailFallback'));
    } catch (requestError) {
      setError(requestError?.status === 404
        ? t('claim.listingGone')
        : t('claim.sendFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitManualClaim = async (event) => {
    event.preventDefault();
    if (!manual.contactName.trim()) return;
    if (!manual.contactEmail.trim() && !manual.contactPhone.trim()) {
      setError(t('claim.contactRequired'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await claimApi.requestProviderClaim(providerId, {
        contactName: manual.contactName.trim(),
        contactEmail: manual.contactEmail.trim() || null,
        contactPhone: manual.contactPhone.trim() || null,
      });
      setManualSubmitted(true);
    } catch (requestError) {
      setError(requestError?.status === 404
        ? t('claim.listingGone')
        : t('claim.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!providerId) {
    return <ClaimShell><Alert severity="error">{t('claim.invalidLink')}</Alert></ClaimShell>;
  }

  if (!currentUser) {
    const returnTo = encodeURIComponent(`/claim/${providerId}`);
    return (
      <ClaimShell>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <VerifiedUserOutlinedIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h5">{t('claim.signInFirstTitle')}</Typography>
          <Typography color="text.secondary" maxWidth={520}>
            {t('claim.signInFirstBody')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} width="100%" justifyContent="center">
            <Button component={RouterLink} to={`/login?returnTo=${returnTo}`} variant="contained" size="large">{t('auth.loginButton')}</Button>
            <Button component={RouterLink} to={`/signup?returnTo=${returnTo}`} variant="outlined" size="large">{t('claim.createAccount')}</Button>
          </Stack>
        </Stack>
      </ClaimShell>
    );
  }

  if (providerData) {
    return (
      <ClaimShell>
        <Alert severity="info" action={<Button component={RouterLink} to="/dashboard">{t('claim.dashboardButton')}</Button>}>
          {t('claim.alreadyManaging')}
        </Alert>
      </ClaimShell>
    );
  }

  return (
    <ClaimShell>
      {loading ? <Box textAlign="center" py={5}><CircularProgress /></Box> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {!loading && !sentTo && !manualSubmitted && emailChannel ? (
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <EmailOutlinedIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h5">{t('claim.emailVerifyTitle')}</Typography>
          <Typography color="text.secondary">
            {t('claim.emailVerifyBody')} <strong>{emailChannel.masked}</strong>. {t('claim.emailVerifyNote')}
          </Typography>
          <Button variant="contained" size="large" onClick={sendClaimEmail} disabled={submitting} fullWidth>
            {submitting ? <CircularProgress size={24} color="inherit" /> : t('claim.sendLinkButton')}
          </Button>
        </Stack>
      ) : null}

      {!loading && !sentTo && !manualSubmitted && channels && !emailChannel ? (
        <Box component="form" onSubmit={submitManualClaim}>
          <Stack spacing={2.25}>
            <Box textAlign="center">
              <BusinessIcon color="primary" sx={{ fontSize: 48 }} />
              <Typography variant="h5" mt={1}>{t('claim.manualTitle')}</Typography>
              <Typography color="text.secondary" mt={1}>
                {t('claim.manualBody')}
              </Typography>
            </Box>
            <TextField required label={t('claim.yourName')} value={manual.contactName} onChange={(e) => setManual({ ...manual, contactName: e.target.value })} inputProps={{ maxLength: 100 }} />
            <TextField required={!manual.contactPhone.trim()} type="email" label={t('claim.contactEmail')} value={manual.contactEmail} onChange={(e) => setManual({ ...manual, contactEmail: e.target.value })} inputProps={{ maxLength: 255 }} />
            <TextField required={!manual.contactEmail.trim()} label={t('claim.contactPhone')} value={manual.contactPhone} onChange={(e) => setManual({ ...manual, contactPhone: e.target.value })} inputProps={{ maxLength: 20 }} />
            <Button type="submit" variant="contained" size="large" disabled={submitting}>
              {submitting ? <CircularProgress size={24} color="inherit" /> : t('claim.submitRequestButton')}
            </Button>
          </Stack>
        </Box>
      ) : null}

      {sentTo ? (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <MarkEmailReadOutlinedIcon color="success" sx={{ fontSize: 56 }} />
          <Typography variant="h5">{t('claim.sentTitle')}</Typography>
          <Typography color="text.secondary">
            {t('claim.sentBody')} <strong>{sentTo}</strong>. {t('claim.sentBodyTail')}
          </Typography>
        </Stack>
      ) : null}

      {manualSubmitted ? (
        <Stack spacing={2} alignItems="center" textAlign="center">
          <VerifiedUserOutlinedIcon color="success" sx={{ fontSize: 56 }} />
          <Typography variant="h5">{t('claim.receivedTitle')}</Typography>
          <Typography color="text.secondary">
            {t('claim.receivedBody')}
          </Typography>
        </Stack>
      ) : null}
    </ClaimShell>
  );
};

function ClaimShell({ children }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, background: 'linear-gradient(145deg, #F5F3FF 0%, #F8FAFC 45%, #EDE9FE 100%)' }}>
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 24px 70px rgba(76, 29, 149, 0.13)' }}>
          <Box sx={{ px: { xs: 3, sm: 5 }, py: 3, color: 'common.white', background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 100%)' }}>
            <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: '0.12em' }}>{t('claim.shellOverline')}</Typography>
            <Typography variant="h4">{t('claim.shellTitle')}</Typography>
          </Box>
          <Box sx={{ p: { xs: 3, sm: 5 } }}>{children}</Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default ClaimBusinessPage;
